import React, { useState, useEffect } from "react";
import { Modal, Checkbox, Button, Row, Col, Space, Tooltip, Typography, message } from "antd";
import { extractValidationErrors } from "../../../utils/apiError";
import { User } from "../../../types/admin/UserTypes";
import { Panel } from "../../../types/geneCodes/Panel";
import StatusTag from "../../shared/StatusTag";
import { HIDDEN_WHEEL_ASSIGNED_NOTE, isHiddenPanel } from "../../../utils/geneCodes/wheelAuthoring";
import {
	fetchPanels,
	getPanelsForUserGLobal,
	updatePanelsForUserGLobal,
} from "../../../api/geneCodes/PanelApi";

const { Text } = Typography;

interface GlobalPanelModalProps {
	user: User;
	isOpen: boolean;
	onClose: () => void;
}

const GlobalPanelModal: React.FC<GlobalPanelModalProps> = ({ user, isOpen, onClose }) => {
	const [panelList, setPanelList] = useState<Panel[]>([]);
	const [selectedPanels, setSelectedPanels] = useState<number[]>([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (isOpen) fetchData();
	}, [isOpen]);

	const fetchData = async () => {
		try {
			const { data } = await fetchPanels(undefined, undefined, undefined, false, "assignable");
			setPanelList(data);
			const userPanels = await getPanelsForUserGLobal(user.email);
			const sp: number[] = userPanels
				.filter((panel: any) => panel.category_id)
				.map((panel: any) => panel.category_id);
			setSelectedPanels(sp);
		} catch (error) {
			console.error("Error fetching panels:", error);
		}
	};

	const handleCheckboxChange = (panelId: number, checked: boolean) => {
		setSelectedPanels((prev) =>
			checked ? [...prev, panelId] : prev.filter((id) => id !== panelId)
		);
	};

	const handleSelectAll = () => {
		setSelectedPanels(
			selectedPanels.length === panelList.length
				? []
				: panelList.map((p) => p.id_panels)
		);
	};

	const handleSubmit = async () => {
		setLoading(true);
		try {
			await updatePanelsForUserGLobal(user.email, selectedPanels);
			onClose();
		} catch (error) {
			console.error("Error updating panels:", error);
			message.error(extractValidationErrors(error).join(' '));
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal
			title="Global Panel Assignment"
			open={isOpen}
			onCancel={onClose}
			width={800}
			footer={[
				<Button key="cancel" onClick={onClose}>Cancel</Button>,
				<Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
					Update Panels
				</Button>,
			]}
		>
			<Space direction="vertical" style={{ width: "100%" }} size="large">
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
					<Text type="secondary">Available Categories</Text>
					<Button onClick={handleSelectAll} size="small">
						{selectedPanels.length === panelList.length ? "Deselect All" : "Select All"}
					</Button>
				</div>
				<Row gutter={[16, 16]}>
					{panelList.map((panel) => (
						<Col xs={24} sm={12} md={8} key={panel.id_panels}>
							<Checkbox
								checked={selectedPanels.includes(panel.id_panels)}
								onChange={(e) => handleCheckboxChange(panel.id_panels, e.target.checked)}
							>
								{panel.name_panel}
							</Checkbox>
							{isHiddenPanel(panel) ? (
								<Tooltip title={HIDDEN_WHEEL_ASSIGNED_NOTE}>
									<span data-testid="global-panel-hidden-tag">
										<StatusTag status="hidden" label="Hidden" color="var(--mg-text-secondary)" size="small" />
									</span>
								</Tooltip>
							) : null}
						</Col>
					))}
				</Row>
			</Space>
		</Modal>
	);
};

export default GlobalPanelModal;
